/*
 * <cart-recommendations>
 *
 * Lazy-loads cross-sell products for the cart drawer using the Shopify Product Recommendations API.
 * Tries `intent=complementary` first (products curated in the Search & Discovery app) and, when the
 * `data-fallback` attribute is "true", falls back to `intent=related` (Shopify's algorithmic
 * recommendations) so the block still shows something when no complementary products are configured.
 *
 * The fetched section (`cart-drawer-recommendations`) renders a `.cart-drawer__recommendations-inner`
 * block, which this element extracts and injects.
 */
class CartRecommendations extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    if (!this.productId || !this.dataset.url) return;

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  loadRecommendations() {
    const intents = ['complementary'];
    if (this.dataset.fallback === 'true') intents.push('related');
    this.fetchIntent(intents, 0);
  }

  fetchIntent(intents, index) {
    if (index >= intents.length) return;

    const intent = intents[index];
    const url = `${this.dataset.url}?intent=${intent}&limit=${this.dataset.limit}&product_id=${this.productId}&section_id=${this.dataset.sectionId}`;

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const recommendations = html.querySelector('.cart-drawer__recommendations-inner');

        if (recommendations && recommendations.querySelector('.cart-drawer__recommendations-item')) {
          this.innerHTML = recommendations.outerHTML;
          this.classList.add('cart-drawer__recommendations--loaded');
        } else {
          // Nothing for this intent — try the next one (complementary → related).
          this.fetchIntent(intents, index + 1);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
